<template>
  <div class="Avatar" :class="[size && `Avatar--${size}`, stateClass]">
    <span class="Avatar-initials">{{ initials }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getInitials } from '@cvo/domain';
import type { AvatarProps } from './Avatar.model';

const props = withDefaults(defineProps<AvatarProps>(), {
  size: 'md',
  active: false,
  disabled: false,
});

const stateClass = computed(() => {
  if (props.disabled) return 'Avatar--disabled';
  if (props.active) return 'Avatar--active';
  return 'Avatar--default';
});

const initials = computed(() => getInitials(props.name));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-width: vars.$atoms-avatar-border-stroke;
    border-style: solid;

    &--md {
      width: 32px;
      height: 32px;
      border-radius: vars.$atoms-avatar-lg-md-border-radius;

      .Avatar-initials {
        @include text.text('sm', vars.$typography-weight-strong);
      }
    }

    &--sm {
      width: 24px;
      height: 24px;
      border-radius: vars.$atoms-avatar-sm-border-radius;

      .Avatar-initials {
        @include text.text('xs', vars.$typography-weight-strong);
      }
    }

    &--default {
      background-color: vars.$atoms-avatar-default-bg-color;
      border-color: vars.$atoms-avatar-default-border-color;

      .Avatar-initials {
        color: vars.$atoms-avatar-default-text-color;
      }
    }

    &--active {
      background-color: vars.$atoms-avatar-active-bg-color;
      border-color: vars.$atoms-avatar-active-border-color;

      .Avatar-initials {
        color: vars.$atoms-avatar-active-text-color;
      }
    }

    &--disabled {
      background-color: vars.$atoms-avatar-disabled-bg-color;
      border-color: vars.$atoms-avatar-disabled-border-color;

      .Avatar-initials {
        color: vars.$atoms-avatar-disabled-text-color;
      }
    }

    &-initials {
      line-height: 1;
    }
  }
}
</style>
